import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';

import { useChartSettings, CHART_HEIGHT_MIN, CHART_HEIGHT_MAX, CHART_HEIGHT_STEP } from 'hooks/use-chart-settings';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 2),
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  slider: {
    paddingTop: theme.spacing(2),
    width: 160,
  },
  heightControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
}));

export function ChartSettingsContent() {
  const classes = useStyles();

  const {
    xAxisTime,
    scrollSpeed,
    allowCreatePointComment,
    jumpToML,
    showCharts,
    visiblePeaks,
    visibleAnnotations,
    visibleIntervals,
    setXAxisTime,
    setScrollSpeed,
    setAllowCreatePointComment,
    setJumpToML,
    setShowCharts,
    setVisiblePeaks,
    setVisibleAnnotations,
    setVisibleIntervals,
    chartHeight,
    setChartHeight,
    resetChartHeight,
  } = useChartSettings();

  const handleVisiblePeaks = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setVisiblePeaks((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleVisibleAnnotations = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setVisibleAnnotations((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleVisibleIntervals = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setVisibleIntervals((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleShowCharts = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setShowCharts((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleSliderChange = (event: any, value: number | number[]) => {
    setScrollSpeed(value as number);
  };

  const label = (text: string) => <span className={classes.noWrap}>{text}</span>;

  return (
    <div className={classes.root}>
      <Grid spacing={2} wrap="nowrap" container>
        {/* Column 1: Chart display */}
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">Chart</FormLabel>
            <FormGroup>
              <Typography variant="caption" style={{ marginTop: 4 }}>Height</Typography>
              <div className={classes.heightControl}>
                <IconButton
                  size="small"
                  disabled={chartHeight <= CHART_HEIGHT_MIN}
                  onClick={() => setChartHeight((h) => Math.max(CHART_HEIGHT_MIN, h - CHART_HEIGHT_STEP))}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" style={{ minWidth: 40, textAlign: 'center' }}>{chartHeight}px</Typography>
                <IconButton
                  size="small"
                  disabled={chartHeight >= CHART_HEIGHT_MAX}
                  onClick={() => setChartHeight((h) => Math.min(CHART_HEIGHT_MAX, h + CHART_HEIGHT_STEP))}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Button size="small" style={{ marginLeft: 4, minWidth: 0, padding: '2px 6px', textTransform: 'none' }} onClick={resetChartHeight}>
                  Reset
                </Button>
              </div>
            </FormGroup>
          </FormControl>

          <div className={classes.slider}>
            <Typography variant="caption" gutterBottom>Autoscroll Speed</Typography>
            <Slider
              value={scrollSpeed}
              aria-labelledby="discrete-slider"
              valueLabelDisplay="auto"
              step={1}
              min={1}
              max={10}
              marks
              onChange={handleSliderChange}
            />
          </div>
        </Grid>

        {/* Column 2: General toggles */}
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">General</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={xAxisTime} color="primary" size="small" onChange={() => setXAxisTime(true)} />}
                label={label('Time (seconds)')}
              />
              <FormControlLabel
                control={<Switch checked={!xAxisTime} color="primary" size="small" onChange={() => setXAxisTime(false)} />}
                label={label('Sample ID')}
              />
              <FormControlLabel
                control={<Switch checked={allowCreatePointComment} color="primary" size="small" onChange={(e) => setAllowCreatePointComment(e.target.checked)} />}
                label={label('Point Comment')}
              />
              <FormControlLabel
                control={<Switch checked={jumpToML} color="primary" size="small" onChange={(e) => setJumpToML(e.target.checked)} />}
                label={label('Jump To ML')}
              />
            </FormGroup>
          </FormControl>
        </Grid>

        {/* Column 3: Peaks & ML */}
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">Peaks</FormLabel>
            <FormGroup>
              <FormControlLabel control={<Switch checked={visiblePeaks.P} name="P" color="primary" size="small" onChange={handleVisiblePeaks} />} label="P" />
              <FormControlLabel control={<Switch checked={visiblePeaks.Q} name="Q" color="primary" size="small" onChange={handleVisiblePeaks} />} label="Q" />
              <FormControlLabel control={<Switch checked={visiblePeaks.R} name="R" color="primary" size="small" onChange={handleVisiblePeaks} />} label="R" />
              <FormControlLabel control={<Switch checked={visiblePeaks.S} name="S" color="primary" size="small" onChange={handleVisiblePeaks} />} label="S" />
              <FormControlLabel control={<Switch checked={visiblePeaks.T} name="T" color="primary" size="small" onChange={handleVisiblePeaks} />} label="T" />
              <FormControlLabel control={<Switch checked={visibleAnnotations.ML} name="ML" color="primary" size="small" onChange={handleVisibleAnnotations} />} label={label('ML auto')} />
            </FormGroup>
          </FormControl>
        </Grid>

        {/* Column 4: Intervals */}
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">Intervals</FormLabel>
            <FormGroup>
              <FormControlLabel control={<Switch checked={visibleIntervals.PQ} name="PQ" color="primary" size="small" onChange={handleVisibleIntervals} />} label="PQ" />
              <FormControlLabel control={<Switch checked={visibleIntervals.QRS} name="QRS" color="primary" size="small" onChange={handleVisibleIntervals} />} label="QRS" />
              <FormControlLabel control={<Switch checked={visibleIntervals.QT} name="QT" color="primary" size="small" onChange={handleVisibleIntervals} />} label="QT" />
              <FormControlLabel control={<Switch checked={visibleIntervals.RR} name="RR" color="primary" size="small" onChange={handleVisibleIntervals} />} label="RR" />
              <FormControlLabel control={<Switch checked={visibleIntervals.U} name="U" color="primary" size="small" onChange={handleVisibleIntervals} />} label={label('Noise')} />
              <FormControlLabel control={<Switch checked={visibleIntervals.AutoNoise} name="AutoNoise" color="primary" size="small" onChange={handleVisibleIntervals} />} label={label('Auto noise')} />
              <FormControlLabel control={<Switch checked={visibleIntervals.AFib} name="AFib" color="primary" size="small" onChange={handleVisibleIntervals} />} label={label('AFib')} />
              <FormControlLabel control={<Switch checked={visibleIntervals.PE} name="PE" color="primary" size="small" onChange={handleVisibleIntervals} />} label={label('Pause Ep.')} />
              <FormControlLabel control={<Switch checked={visibleIntervals.Apnea} name="Apnea" color="primary" size="small" onChange={handleVisibleIntervals} />} label={label('Apnea')} />
            </FormGroup>
          </FormControl>
        </Grid>

        {/* Column 5: Additional charts */}
        <Grid item>
          <FormControl component="fieldset">
            <FormLabel component="legend">Extra charts</FormLabel>
            <FormGroup>
              <FormControlLabel control={<Switch checked={showCharts.rate} name="rate" color="primary" size="small" onChange={handleShowCharts} />} label={label('HR Line')} />
              <FormControlLabel control={<Switch checked={showCharts.histogram} name="histogram" color="primary" size="small" onChange={handleShowCharts} />} label={label('HR Histogram')} />
              <FormControlLabel control={<Switch checked={showCharts.interval} name="interval" color="primary" size="small" onChange={handleShowCharts} />} label={label('Intervals')} />
              <FormControlLabel control={<Switch checked={showCharts.annotation} name="annotation" color="primary" size="small" onChange={handleShowCharts} />} label={label('Annotations')} />
              <FormControlLabel control={<Switch checked={showCharts.tachycardia} name="tachycardia" color="primary" size="small" onChange={handleShowCharts} />} label={label('Tachycardia')} />
              <FormControlLabel control={<Switch checked={showCharts.healthIndication} name="healthIndication" color="primary" size="small" onChange={handleShowCharts} />} label={label('Health Ind.')} />
            </FormGroup>
          </FormControl>

        </Grid>
      </Grid>
    </div>
  );
}
